
import { useEffect } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SectionTitle } from "@/components/SectionTitle";
import { CTASection } from "@/components/CTASection";
import { Button } from "@/components/ui/button";
import { TreatmentServiceCard } from "@/components/TreatmentServiceCard";
import { FAQAccordion } from "@/components/FAQAccordion";
import { ServiceTestimonials } from "@/components/ServiceTestimonials";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { PageTitle } from "@/components/PageTitle";

const PediatricDentistry = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // FAQs specific to Pediatric Dentistry
  const faqs = [
    {
      question: "When should my child have their first dental visit?",
      answer: "The American Academy of Pediatric Dentistry recommends that children see a dentist by their first birthday or when their first tooth appears. Early visits help establish a dental home and allow us to monitor development, provide preventive care, and educate parents on proper oral hygiene practices for their children."
    },
    {
      question: "How can I prepare my child for their dental visit?",
      answer: "Keep explanations simple and positive. Avoid using words that might cause unnecessary fear, such as 'hurt', 'pain', or 'shot'. Read children's books about dental visits, play pretend dentist at home, and emphasize that the dentist helps keep teeth healthy. Let your child know that the dentist will count and clean their teeth, and might take pictures of them."
    },
    {
      question: "Are dental X-rays safe for children?",
      answer: "Yes, dental X-rays are safe for children. We use digital X-rays that minimize radiation exposure, and protective equipment such as lead aprons and thyroid collars provide additional safety. X-rays are only taken when necessary to diagnose conditions not visible during a clinical examination."
    },
    {
      question: "How can I prevent tooth decay in my child?",
      answer: "Establish good oral hygiene habits early: brush twice daily with a child-sized toothbrush and fluoride toothpaste (rice-sized amount for children under 3, pea-sized for older children). Limit sugary foods and drinks, avoid putting your child to bed with a bottle, and schedule regular dental check-ups. Fluoride treatments and dental sealants can also provide additional protection."
    },
    {
      question: "What should I do if my child has a dental emergency?",
      answer: "For a knocked-out permanent tooth, hold it by the crown (not the root), rinse it gently without scrubbing, and try to reinsert it in the socket. If that's not possible, place it in milk and seek immediate dental care. For other emergencies like toothaches, broken teeth, or injuries, call our office right away. We reserve time for emergencies and will see your child as soon as possible."
    }
  ];

  // Testimonials for Pediatric Dentistry
  const testimonials = [
    {
      name: "Neha Gupta",
      comment: "My son was extremely anxious about going to the dentist after a bad experience elsewhere. The team at Asrani Dental was so patient and made him feel completely at ease. They explained everything in child-friendly terms and turned the visit into a positive experience. Now he actually looks forward to his dental check-ups!",
      rating: 5
    },
    {
      name: "Aditya Singh",
      comment: "Our daughter needed treatment for several cavities, and we were worried about how she would handle it. Dr. Asrani and his team were amazing - they were gentle, patient, and used techniques that made her feel comfortable. The child-friendly environment with toys and activities made a huge difference in her attitude towards dental care.",
      rating: 5
    },
    {
      name: "Priya Reddy",
      comment: "The preventive approach at Asrani Dental has made such a difference for my children's oral health. The dental hygienist took extra time to teach proper brushing techniques in a fun way, and the dentist explained the importance of healthy eating habits for strong teeth. Their focus on education has helped us establish good habits at home.",
      rating: 5
    }
  ];

  const handleBookAppointment = () => {
    window.open("https://www.healthcoco.com/nagpur/doctor/dr-hemant-asrani-endodontist-dentist/USRH249D644", "_blank");
  };

  return (
    <div className="min-h-screen">
      <PageTitle title="Kids Dentistry in Nagpur | Cost of Kids Dentistry in Nagpur" />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 bg-dental-offwhite">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block text-dental-highlight font-medium mb-4 uppercase tracking-wider text-sm">
              Our Services
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-semibold mb-6">
              Pediatric Dentistry
            </h1>
            <p className="text-lg text-dental-muted">
              Specialized dental care for children in a friendly, comfortable environment designed to make dental visits enjoyable.
            </p>
            <div className="mt-8">
              <Button className="btn-primary" onClick={handleBookAppointment}>
                Book Appointment
              </Button>
            </div>
          </div>
        </div>
      </section>
      
      {/* Services Overview */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="/lovable-uploads/f21f54d9-d1a7-4415-b23a-cd0567886562.png" 
                alt="Pediatric Dentistry at Asrani Dental Clinic" 
                className="rounded-xl shadow-soft w-full h-auto"
              />
            </div>
            
            <div>
              <SectionTitle 
                title="Child-Friendly Dental Care" 
                subtitle="Pediatric Dentistry"
                description="Our pediatric dental services are designed specifically for children, creating positive dental experiences that set the foundation for a lifetime of good oral health."
                alignment="left"
              />
              
              <div className="mt-8 space-y-4">
                <div className="flex items-start">
                  <CheckCircle2 size={24} className="text-dental-highlight mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-medium mb-1">Child-Focused Approach</h3>
                    <p className="text-dental-muted">Age-appropriate care with a gentle touch and language that children can understand and relate to.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle2 size={24} className="text-dental-highlight mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-medium mb-1">Comfortable Environment</h3>
                    <p className="text-dental-muted">A welcoming, fun atmosphere with child-friendly décor, toys, and entertainment to reduce anxiety.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle2 size={24} className="text-dental-highlight mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-medium mb-1">Preventive Focus</h3>
                    <p className="text-dental-muted">Emphasis on education and preventive care to establish good habits early and avoid future problems.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle2 size={24} className="text-dental-highlight mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-medium mb-1">Growth Monitoring</h3>
                    <p className="text-dental-muted">Regular assessment of dental and facial development to identify and address potential issues early.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Treatments Section */}
      <section className="py-16 md:py-24 bg-dental-cream">
        <div className="container-custom">
          <SectionTitle 
            title="Our Pediatric Dental Services" 
            subtitle="Services We Offer"
            description="We provide comprehensive dental care for children of all ages, from infants to teenagers."
            alignment="center"
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            <TreatmentServiceCard 
              title="Dental Check-ups & Cleanings"
              description="Regular examinations and gentle cleanings to monitor development and maintain oral health from an early age."
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2" className="w-12 h-12">
                  <path d="M32 7.5C26.5 7.5 21.5 8.5 16.5 12.5C12 16 8.5 20 8.5 27.5C8.5 35 9.5 40 11.5 45C13.5 50 16.5 53.5 21.5 56C24 57.2 28 57.5 32 57.5C36 57.5 40 57.2 42.5 56C47.5 53.5 50.5 50 52.5 45C54.5 40 55.5 35 55.5 27.5C55.5 20 52 16 47.5 12.5C42.5 8.5 37.5 7.5 32 7.5Z" />
                  <path d="M24 23C26 21 28 20 32 20C36 20 38 21 40 23C42 25 43 28 43 32C43 36 42 39 40 41C38 43 36 44 32 44C28 44 26 43 24 41C22 39 21 36 21 32C21 28 22 25 24 23Z" />
                  <path d="M32 20V44" />
                  <path d="M21 32H43" />
                </svg>
              }
            />
            
            <TreatmentServiceCard 
              title="Preventive Treatments"
              description="Fluoride applications, dental sealants, and other preventive measures to protect developing teeth from decay."
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2" className="w-12 h-12">
                  <path d="M19 8C16 8 13.5 10.5 13.5 13.5C13.5 16.5 16 19 19 19H45C48 19 50.5 16.5 50.5 13.5C50.5 10.5 48 8 45 8H19Z" />
                  <path d="M32 19V27" />
                  <circle cx="32" cy="32" r="5" />
                  <path d="M18 40H22" />
                  <path d="M26 40H30" />
                  <path d="M34 40H38" />
                  <path d="M42 40H46" />
                  <path d="M18 46H22" />
                  <path d="M26 46H30" />
                  <path d="M34 46H38" />
                  <path d="M42 46H46" />
                  <path d="M18 52H22" />
                  <path d="M26 52H30" />
                  <path d="M34 52H38" />
                  <path d="M42 52H46" />
                </svg>
              }
            />
            
            <TreatmentServiceCard 
              title="Pediatric Fillings & Restorations"
              description="Tooth-colored fillings and other restorative procedures specially adapted for children's needs and comfort."
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2" className="w-12 h-12">
                  <path d="M16 10C16 10 14 17 14 25C14 37 21 54 32 54C43 54 50 37 50 25C50 17 48 10 48 10H16Z" />
                  <path d="M16 10C16 10 24 15 32 15C40 15 48 10 48 10" />
                  <path d="M40 29L44 25" />
                </svg>
              }
            />
            
            <TreatmentServiceCard 
              title="Early Orthodontic Assessment"
              description="Evaluation of dental and facial development to identify potential orthodontic issues that may benefit from early intervention."
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2" className="w-12 h-12">
                  <path d="M14 16C14 16 12 23 12 30C12 41 18 52 26 52C30 52 33 48 33 44V20C33 16 30 12 26 12C18 12 14 16 14 16Z" />
                  <circle cx="44" cy="40" r="10" />
                  <path d="M44 35V45" />
                  <path d="M39 40H49" />
                </svg>
              }
            />
            
            <TreatmentServiceCard 
              title="Habit Counseling"
              description="Guidance and treatments to address thumb sucking, pacifier use, and other habits that can affect dental development."
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2" className="w-12 h-12">
                  <path d="M16 8C16 8 14 15 14 23C14 35 19 52 28 52" />
                  <path d="M48 8C48 8 50 15 50 23C50 35 45 52 36 52" />
                  <path d="M16 8C16 8 24 15 32 15C40 15 48 8 48 8" />
                  <path d="M28 52C28 52 30 48 32 48C34 48 36 52 36 52" />
                  <path d="M36 22C38 20 41 20 43 22C45 24 45 27 43 29C41 31 38 31 36 29" />
                </svg>
              }
            />
            
            <TreatmentServiceCard 
              title="Emergency Pediatric Dental Care"
              description="Prompt, compassionate care for dental injuries, toothaches, and other urgent dental issues in children."
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2" className="w-12 h-12">
                  <path d="M12 14C12 14 10 25 10 32C10 44 16 56 24 56C30 56 32 52 32 48V16C32 12 30 8 24 8C16 8 12 14 12 14Z" />
                  <path d="M52 14C52 14 54 25 54 32C54 44 48 56 40 56C34 56 32 52 32 48V16C32 12 34 8 40 8C48 8 52 14 52 14Z" />
                  <path d="M12 14C12 14 18 22 32 22C46 22 52 14 52 14" />
                </svg>
              }
            />
          </div>
        </div>
      </section>
      
      {/* Testimonials Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-custom">
          <SectionTitle 
            title="Parent Testimonials" 
            subtitle="What Parents Say"
            description="Hear from parents about their children's experiences with our pediatric dental services."
            alignment="center"
          />
          
          <div className="max-w-4xl mx-auto mt-12">
            <ServiceTestimonials testimonials={testimonials} />
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-dental-offwhite">
        <div className="container-custom">
          <SectionTitle 
            title="Frequently Asked Questions" 
            subtitle="Common Questions"
            description="Find answers to common questions about pediatric dental care."
            alignment="center"
          />
          
          <div className="max-w-3xl mx-auto mt-12">
            <FAQAccordion faqs={faqs} />
          </div>
        </div>
      </section>
      
      {/* Next Steps Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-serif font-semibold mb-6">Ready to Schedule Your Child's Visit?</h2>
            <p className="text-lg text-dental-muted mb-8">
              Contact our office today to schedule your child's dental appointment. We're dedicated to making dental visits a positive experience for children of all ages.
            </p>
            
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button className="btn-primary flex items-center text-base group" onClick={handleBookAppointment}>
                Book Appointment
                <ArrowRight size={18} className="ml-2 transition-transform duration-300 group-hover:translate-x-1" />
              </Button>
              
              <Button className="btn-secondary flex items-center text-base" onClick={() => window.location.href = '/contact'}>
                Contact Us
              </Button>
            </div>
            
            <div className="mt-10">
              <p className="text-dental-muted mb-4">Interested in our other dental services?</p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/services/general-dentistry" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  General Dentistry
                </Link>
                <Link to="/services/cosmetic-dentistry" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  Cosmetic Dentistry
                </Link>
                <Link to="/services/orthodontics" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  Orthodontics
                </Link>
                <Link to="/services/full-mouth-implants" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  Full Mouth & Implants
                </Link>
                <Link to="/services/painless-rct-in-nagpur" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  Painless RCT
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <CTASection
        title="Give Your Child the Gift of a Healthy Smile"
        description="Our specialized pediatric dental services create positive experiences that set the foundation for a lifetime of good oral health. Schedule your child's appointment today."
        primaryButtonText="Book an Appointment"
        secondaryButtonText="Learn More"
        primaryButtonAction={handleBookAppointment}
        secondaryButtonAction={() => window.location.href = "/about"}
      />
      
      <Footer />
    </div>
  );
};

export default PediatricDentistry;
